/**
 * @module components/analysis/CraftTypeToggle
 * @description Music/Books segmented control with trust-indicator dots.
 *
 * @extracted-from client/src/App.js (lines ~1142-1251)
 */

import React from 'react';
import { Mic, FileText } from 'lucide-react';
import { SegmentedControl } from '../ui';

/**
 * @param {Object} props
 * @param {'music'|'book'} props.craftType
 * @param {(craftType: 'music'|'book') => void} props.onCraftTypeChange
 */
const CraftTypeToggle = ({ craftType, onCraftTypeChange }) => {
  return (
    <>
      <div className="mt-12 md:mt-8 mb-12 md:mb-8 flex justify-center">
        <SegmentedControl
          ariaLabel="Craft type"
          layout="equal"
          className="w-auto inline-flex p-1"
          value={craftType}
          onChange={onCraftTypeChange}
          options={[
            {
              value: 'music',
              label: <span className="font-display text-sm md:text-base">Music</span>,
              icon: (
                <Mic
                  className={`h-5 w-5 transition-all duration-critic ${
                    craftType === 'music' ? 'text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.4)]' : ''
                  }`}
                  aria-hidden
                />
              ),
            },
            {
              value: 'book',
              label: <span className="font-display text-sm md:text-base">Books</span>,
              icon: (
                <FileText
                  className={`h-5 w-5 transition-all duration-critic ${
                    craftType === 'book' ? 'text-white drop-shadow-[0_0_12px_rgba(255,255,255,0.4)]' : ''
                  }`}
                  aria-hidden
                />
              ),
            },
          ]}
        />
      </div>

      <div className="mt-12 md:mt-8 mb-10 md:mb-8">
        <div
          className={`flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-8 lg:space-x-12 transition-all duration-500 ${
            craftType === 'music' ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          } ${craftType === 'book' ? 'hidden' : 'block'}`}
        >
          <div className="flex items-center space-x-3 animate-fade-in-up">
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-emerald-400/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_1px_rgba(52,211,153,0.5),0_0_14px_5px_rgba(16,185,129,0.45),0_0_28px_10px_rgba(52,211,153,0.2)] animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Technical quality
            </span>
          </div>
          <div className="flex items-center space-x-3 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-apple-blue/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-apple-blue shadow-[0_0_0_1px_rgba(0,122,255,0.55),0_0_14px_5px_rgba(0,122,255,0.5),0_0_28px_10px_rgba(0,122,255,0.22)] animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Structure & composition
            </span>
          </div>
          <div className="flex items-center space-x-3 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-critic-insight-glow/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-critic-insight-purple glow-insight-dot animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Market readiness
            </span>
          </div>
        </div>

        <div
          className={`flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-8 lg:space-x-12 transition-all duration-500 ${
            craftType === 'book' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          } ${craftType === 'music' ? 'hidden' : 'block'}`}
        >
          <div className="flex items-center space-x-3 animate-fade-in-up">
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-emerald-400/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_1px_rgba(52,211,153,0.5),0_0_14px_5px_rgba(16,185,129,0.45),0_0_28px_10px_rgba(52,211,153,0.2)] animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Narrative structure
            </span>
          </div>
          <div className="flex items-center space-x-3 animate-fade-in-up" style={{ animationDelay: '0.08s' }}>
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-apple-blue/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-apple-blue shadow-[0_0_0_1px_rgba(0,122,255,0.55),0_0_14px_5px_rgba(0,122,255,0.5),0_0_28px_10px_rgba(0,122,255,0.22)] animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Character & voice
            </span>
          </div>
          <div className="flex items-center space-x-3 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
            <span className="relative flex h-3 w-3 flex-shrink-0 items-center justify-center" aria-hidden>
              <span className="absolute inset-0 rounded-full bg-critic-insight-glow/35 blur-[6px] animate-pulse" />
              <span className="relative h-2 w-2 rounded-full bg-critic-insight-purple glow-insight-dot animate-pulse" />
            </span>
            <span className="text-sm md:text-base text-critic-text-secondary font-normal">
              Readiness & positioning
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CraftTypeToggle;
